import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ChartOptions } from 'chart.js';
import { PageHeader } from '@/components/common/PageHeader';
import { LineChart } from '@/components/charts/LineChart';
import { useToast } from '@/components/common/toast';
import { formatMoney, pnlClass } from '@/utils/format';
import { LoginOverviewModal } from '@/features/tenant/loginOverview/LoginOverviewModal';
import {
  riskDashboardApi,
  todayDateString,
  yesterdayDateString,
  toClientProfitabilityInput,
  toNopFiltersDto,
} from '@/api/services/riskDashboard';
import type {
  EodNetInstrumentDto,
  LpHubAccountDto,
  RiskDashboardFilters,
  RiskDashboardLiveDto,
} from '@/api/types/riskDashboard';
import { useLpAccountsLive, useRiskDashboardLive } from './useRiskDashboardLive';
import { usePermission } from '@/permissions/usePermission';
import { InstrumentsTab, OpenPositionsTable, type InstrumentFilterHandlers } from './InstrumentsTab';
import { ClientAccountsTable, LpAccountsTable, mergeClientAccounts } from './AccountsTab';
import { RiskDashboardFilterBar } from './RiskDashboardFilterBar';
import { RiskDashboardPermissions } from './permissions';
import './riskDashboard.css';

type Tab = 'instruments' | 'accounts';
type KpiKey = 'firm' | 'clients' | 'lp';

// Which chart dataset is emphasised for the selected KPI card (legacy FOCUS_MATCH).
const FOCUS_MATCH: Record<KpiKey, RegExp> = {
  firm: /net firm|firm net/i,
  clients: /clients/i,
  lp: /\blp\b/i,
};
function isFocusDataset(kpi: KpiKey, label: string): boolean {
  return (FOCUS_MATCH[kpi] ?? FOCUS_MATCH.firm).test(label);
}

// K/M/B compact y-axis ticks (legacy risk-dashboard-kpi._yTickCallback).
function kpiYTick(value: number | string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

const kpiChartOptions: ChartOptions<'line'> = {
  interaction: { mode: 'index', intersect: false },
  plugins: { legend: { display: false } },
  scales: {
    x: { grid: { display: false } },
    y: {
      beginAtZero: false,
      grid: { color: 'rgba(0,0,0,0.05)' },
      ticks: { callback: (value) => kpiYTick(value) },
    },
  },
};

// YYYY-MM-DD -> DD/MM/YYYY for the PnL chart date-range badge.
function toDisplayDate(iso: string): string {
  const [y, m, d] = (iso ?? '').split('-');
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

function defaultFilters(): RiskDashboardFilters {
  const today = todayDateString();
  return {
    startDate: today,
    endDate: today,
    login: null,
    uniqueId: null,
    platformId: null,
    instrument: null,
    mifidFilter: null,
    groupName: null,
    groupTextFilter: null,
    groupProfile: null,
    executionVenue: null,
    countryId: null,
    ifSwaps: null,
    symbolMapping: false,
    portfolioId: null,
    byUniqueId: false,
    liveMode: true,
    showLp: true,
  };
}

export function RiskDashboardPage() {
  const toast = useToast();
  const can = usePermission();
  const [draft, setDraft] = useState<RiskDashboardFilters>(defaultFilters);
  const [applied, setApplied] = useState<RiskDashboardFilters>(draft);
  const [activeTab, setActiveTab] = useState<Tab>('instruments');
  const [live, setLive] = useState<RiskDashboardLiveDto | null>(null);
  const [lpRows, setLpRows] = useState<LpHubAccountDto[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [chart, setChart] = useState<{ open: boolean; kpi: KpiKey }>({ open: false, kpi: 'firm' });
  const [loModal, setLoModal] = useState<{ login: number; connector: number } | null>(null);

  // Snapshot gate: in Live-OFF mode the exposure hub is still used, but only ONE
  // packet is applied per Fetch, then further pushes are ignored (mirrors legacy
  // awaitingSnapshot / shouldApplyNopData). Live-ON applies every packet.
  const awaitingSnapshot = useRef(false);

  // REST enrichment — drives the Client Accounts table + KPI fallback totals.
  const enrichment = useQuery({
    queryKey: ['rd-enrichment', applied],
    queryFn: () => riskDashboardApi.getClientAccountsEnrichment(toClientProfitabilityInput(applied)),
  });

  // Expected EOD swap snapshot (per-instrument), scoped to the applied filters.
  const eodQuery = useQuery({
    queryKey: ['rd-eod', applied],
    queryFn: () => riskDashboardApi.getExpectedEodSwap(toNopFiltersDto(applied)),
  });
  const eodBySymbol = useMemo(() => {
    const m = new Map<string, EodNetInstrumentDto>();
    (eodQuery.data?.netRows ?? []).forEach((r) => m.set((r.instrument ?? '').toUpperCase(), r));
    return m;
  }, [eodQuery.data]);

  const onLiveData = useCallback(
    (data: RiskDashboardLiveDto) => {
      if (applied.liveMode) {
        setLive(data);
        setLastUpdated(new Date());
      } else if (awaitingSnapshot.current) {
        setLive(data);
        setLastUpdated(new Date());
        awaitingSnapshot.current = false; // freeze until the next Fetch
      }
    },
    [applied.liveMode],
  );
  const onLiveError = useCallback((m: string) => toast.error(m), [toast]);

  const { status, updateFilters } = useRiskDashboardLive({
    enabled: true,
    initialFilters: toNopFiltersDto(applied),
    onData: onLiveData,
    onError: onLiveError,
  });

  useLpAccountsLive({
    enabled: applied.showLp,
    onAccounts: setLpRows,
    onError: onLiveError,
  });

  // Push filter changes to the live hub whenever the applied filters change, and
  // arm one snapshot (used only in Live-OFF mode) + a loading overlay + 30s timeout.
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    awaitingSnapshot.current = true;
    updateFilters(toNopFiltersDto(applied));
    setLoading(true);
    const timeout = window.setTimeout(() => {
      setLoading(false);
      toast.error('Live exposure is taking longer than expected. Check connection status and try Fetch again.');
    }, 30_000);
    return () => window.clearTimeout(timeout);
  }, [applied, updateFilters, toast]);

  // Clear the loading overlay once the first data arrives (live push or snapshot).
  useEffect(() => {
    if (lastUpdated) setLoading(false);
  }, [lastUpdated]);

  // Reconnection toasts (mirrors risk-dashboard-connection.js).
  const prevStatus = useRef(status);
  useEffect(() => {
    const prev = prevStatus.current;
    if (status === 'reconnecting' && prev !== 'reconnecting') {
      toast.error('Risk Dashboard live connection lost. Reconnecting...');
    } else if (status === 'connected' && (prev === 'reconnecting' || prev === 'disconnected')) {
      toast.success('Risk Dashboard live connection restored.');
    }
    prevStatus.current = status;
  }, [status, toast]);

  const fetch = () => setApplied({ ...draft });
  const clear = () => {
    const d = defaultFilters();
    setDraft(d);
    setApplied(d);
  };

  // Mode chips (Live / Show LP / AutoMap / UID) take effect immediately, as in
  // legacy — unlike the data filters, which wait for Fetch. Toggling Live also
  // moves the end date (today when ON, yesterday when OFF) per FinkitLiveDateSync.
  const toggleMode = (key: keyof RiskDashboardFilters, value: boolean) => {
    if (key === 'liveMode') {
      const newEnd = value ? todayDateString() : yesterdayDateString();
      const apply = (f: RiskDashboardFilters): RiskDashboardFilters => ({
        ...f,
        liveMode: value,
        endDate: newEnd,
        startDate: f.startDate > newEnd ? newEnd : f.startDate,
      });
      setDraft(apply);
      setApplied(apply);
      return;
    }
    setDraft((f) => ({ ...f, [key]: value }));
    setApplied((f) => ({ ...f, [key]: value }));
  };

  // Double-click a contributor login/uniqueId → scope the dashboard to it (mirrors
  // _bindContributorFilterClicks in risk-dashboard-exposure.js).
  const instrumentHandlers: InstrumentFilterHandlers = {
    onFilterLogin: (login, platformId) => {
      const next = { ...draft, login, uniqueId: null, platformId: platformId ?? draft.platformId };
      setDraft(next);
      setApplied(next);
    },
    onFilterUniqueId: (uniqueId) => {
      const next = { ...draft, uniqueId, login: null };
      setDraft(next);
      setApplied(next);
    },
  };

  // KPI: live push wins when present, else REST enrichment totals.
  const kpi = useMemo(() => {
    const e = enrichment.data;
    const k = live?.kpi;
    return {
      firm: k?.firmNetPnL ?? e?.firmNetPnL ?? null,
      clients: k?.clientsPnL ?? e?.clientsPnL ?? null,
      lp: k?.lpPnL ?? e?.lpPnL ?? null,
    };
  }, [live, enrichment.data]);

  const chartQuery = useQuery({
    queryKey: ['rd-firm-pnl-chart', applied],
    queryFn: () => riskDashboardApi.getFirmNetPnlChartData(toClientProfitabilityInput(applied)),
    enabled: chart.open,
  });

  const instruments = live?.instruments ?? [];
  // Client tab is LIVE: overlay live NOP client-account rows onto the REST enrichment,
  // pruning to the live login universe when an instrument filter is set (gap 2/3,
  // mirrors risk-dashboard-accounts._mergeNopWithCp).
  const clientAccounts = useMemo(
    () =>
      mergeClientAccounts(
        enrichment.data?.items ?? [],
        live?.clientAccounts ?? [],
        !!(applied.instrument && applied.instrument.trim()),
      ),
    [enrichment.data, live?.clientAccounts, applied.instrument],
  );

  // Status precedence mirrors legacy risk-dashboard-state._updateConnectionUi: a received
  // snapshot (live != null, i.e. lastNopData) keeps the green Connected/Snapshot label even
  // if the socket has since dropped. "Live" requires liveMode AND a connected socket.
  const hasData = live != null;
  const statusLabel =
    applied.liveMode && status === 'connected' ? 'Live'
      : status === 'connected' || hasData ? (applied.liveMode ? 'Connected' : 'Snapshot')
        : status === 'connecting' || status === 'reconnecting' ? 'Connecting' : 'Disconnected';
  const statusDot =
    (applied.liveMode && status === 'connected') || status === 'connected' || hasData ? 'rd-status-connected'
      : status === 'connecting' || status === 'reconnecting' ? 'rd-status-connecting' : 'rd-status-disconnected';

  return (
    <div id="risk-dashboard-page" className="risk-dashboard-page">
      <PageHeader title="Dashboard" breadcrumb={['Home', 'Dashboard']} />

      {/* Zone A — filter bar + connection status */}
      <RiskDashboardFilterBar
        draft={draft}
        setDraft={setDraft}
        onFetch={fetch}
        onClear={clear}
        onModeToggle={toggleMode}
        canAutoMap={can(RiskDashboardPermissions.autoMap)}
        canByUniqueId={can(RiskDashboardPermissions.byUniqueId)}
        statusSlot={
          <div className="rd-connection-bar d-flex align-items-center gap-2">
            <span className={`rd-status-dot ${statusDot}`} />
            <span className="fs-11 fw-semibold text-uppercase">{statusLabel}</span>
            <span className="fs-12 font-monospace text-muted">
              {lastUpdated ? lastUpdated.toLocaleTimeString([], { hour12: false }) : '--:--:--'}
            </span>
          </div>
        }
      />

      {/* Zone B — KPI cards */}
      <div className="row g-2 mb-2">
        {([
          { key: 'firm', label: 'Firm Net PnL', value: kpi.firm, icon: 'ri-funds-line' },
          { key: 'clients', label: 'Clients PnL', value: kpi.clients, icon: 'ri-group-line' },
          { key: 'lp', label: 'LP PnL', value: kpi.lp, icon: 'ri-exchange-line' },
        ] as { key: KpiKey; label: string; value: number | null; icon: string }[]).map((c) => (
          <div className="col-lg-4 col-sm-12" key={c.key}>
            <div
              className={`card overflow-hidden rd-kpi-card mb-0${chart.open && chart.kpi === c.key ? ' rd-kpi-active' : ''}`}
              role="button"
              title="Show PnL history"
              onClick={() =>
                setChart((prev) => (prev.open && prev.kpi === c.key ? { open: false, kpi: c.key } : { open: true, kpi: c.key }))
              }
            >
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div className="min-w-0">
                    <div className={`fw-semibold fs-20 text-truncate ${pnlClass(c.value)}`}>
                      {c.value == null ? 'N/A' : formatMoney(c.value)}
                    </div>
                    <div className="text-muted">{c.label}</div>
                    <div className="fs-11 text-muted">Click for history</div>
                  </div>
                  <div className="counter-icon dash bg-primary rounded-circle d-flex align-items-center justify-content-center flex-shrink-0">
                    <i className={`text-white ${c.icon}`} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {chart.open && (
        <div className="card custom-card mb-2">
          <div className="card-header justify-content-between align-items-center">
            <div className="card-title mb-0">
              <i className="fe fe-trending-up me-2 text-primary" />
              PnL History
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-primary-transparent text-primary rd-pnl-range">
                {applied.startDate === applied.endDate
                  ? toDisplayDate(applied.startDate)
                  : `${toDisplayDate(applied.startDate)} – ${toDisplayDate(applied.endDate)}`}
              </span>
              <button type="button" className="btn-close btn-sm" onClick={() => setChart((c) => ({ ...c, open: false }))} />
            </div>
          </div>
          <div className="card-body rd-pnl-chart-body" style={{ minHeight: 260 }}>
            {chartQuery.data && chartQuery.data.labels?.length ? (
              <>
                <div className="rd-pnl-chart flex-fill">
                  <LineChart
                    labels={chartQuery.data.labels}
                    series={chartQuery.data.datasets.map((d) => ({
                      label: d.label ?? '',
                      data: d.data,
                      color: d.borderColor ?? undefined,
                      emphasis: isFocusDataset(chart.kpi, d.label ?? ''),
                    }))}
                    options={kpiChartOptions}
                  />
                </div>
                <div className="rd-pnl-legend">
                  {chartQuery.data.datasets.map((d, i) => (
                    <div className="rd-pnl-legend-item" key={`${d.label}-${i}`}>
                      <span className="rd-pnl-legend-line" style={{ background: d.borderColor ?? '#8fbd56' }} />
                      <span className="rd-pnl-legend-label">{d.label}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="rd-pnl-chart flex-fill">
                <LineChart labels={[]} series={[]} height={220} emptyHint={chartQuery.isLoading ? 'Loading…' : 'No chart data for the selected date range.'} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Zone C — tabs (rd-finkit-tabs → green active pill, per risk-dashboard.css) */}
      <div className="rd-finkit-tabs mb-2">
        <ul className="nav nav-pills" role="tablist">
          <li className="nav-item">
            <button type="button" className={`nav-link${activeTab === 'instruments' ? ' active' : ''}`} onClick={() => setActiveTab('instruments')}>
              Instruments
            </button>
          </li>
          <li className="nav-item">
            <button type="button" className={`nav-link${activeTab === 'accounts' ? ' active' : ''}`} onClick={() => setActiveTab('accounts')}>
              Accounts
            </button>
          </li>
        </ul>
      </div>

      <div className="position-relative">
        {loading && (
          <div className="rd-loading-overlay">
            <div className="spinner-border text-primary" role="status" />
          </div>
        )}
        {activeTab === 'instruments' ? (
          <InstrumentsTab
            rows={instruments}
            handlers={instrumentHandlers}
            filters={applied}
            eodBySymbol={eodBySymbol}
            showLp={applied.showLp}
          />
        ) : (
          <div className="row g-2">
            <div className={applied.showLp ? 'col-lg-6' : 'col-lg-12'}>
              <ClientAccountsTable
                rows={clientAccounts}
                loading={enrichment.isLoading && clientAccounts.length === 0}
                error={enrichment.isError && clientAccounts.length === 0 ? 'Missing Risk Dashboard permission for Client Accounts enrichment.' : null}
                onOpenLogin={(login, platformId) => setLoModal({ login, connector: platformId })}
              />
            </div>
            {applied.showLp && (
              <div className="col-lg-6">
                <LpAccountsTable
                  nopRows={live?.lpAccounts ?? []}
                  hubRows={lpRows}
                  platformFilter={applied.platformId}
                  dates={{ startDate: applied.startDate, endDate: applied.endDate }}
                  onOpenLogin={(login, platformId) => setLoModal({ login, connector: platformId })}
                  buildContributors={(details) => (
                    <OpenPositionsTable
                      details={details}
                      digits={2}
                      showInstrument
                      emptyMessage="No contributing open positions for this login."
                    />
                  )}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {loModal && (
        <LoginOverviewModal login={loModal.login} connector={loModal.connector} onClose={() => setLoModal(null)} />
      )}
    </div>
  );
}

